import { useModelConfig } from '@/hooks/useModelConfig'
import './forecastStyles.css'

import { CaretDownFilled, CaretUpFilled } from '@ant-design/icons'
import { useSiteMonitoring } from '@api/useSiteMonitoring'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import {
  getColumnDefs,
  hourliesColumnDefs,
} from '@components/StoreMonitoring/StoreDetailsPage/TabView/Forecast/columnDefs'
import { ErrorNotification, GraviGrid, Texto, Vertical, addCommasToNumber } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { isDefined } from '@utils/general'
import { formatters } from '@utils/intl/dateTimeFormatters'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export function Forecasts({ selectedTank, selectedManifold, store, showProjected }) {
  const [visiblePopoverId, setVisiblePopoverId] = useState()
  const gridAPIRef = useRef()
  const {
    useSiteDetailsForecastQuery,
    useForecastOverrideMutation,
    useDeleteForecastOverrideMutation,
    useSalesOverrideMutation,
    useDeleteSalesOverrideMutation,
  } = useSiteMonitoring()
  const { config } = useModelConfig()

  const store_number = useMemo(() => store?.store_number, [store])
  const filters = useMemo(
    () => ({
      store_number,
      tank_id:
        selectedManifold && selectedManifold.store_number === store_number
          ? selectedManifold.manifold_id
          : selectedTank?.store_number === store_number
            ? selectedTank.tank_id
            : null,
    }),
    [selectedTank, selectedManifold, store_number]
  )

  const { data: forecastData, fetchStatus } = useSiteDetailsForecastQuery(filters)

  // Grid is acting weird with loading states for some reason - hack to manually control loading overlay
  // TODO: Need to figure out why we're getting stuck in loading state after overrides (maybe in excalibrr)
  useEffect(() => {
    if (!gridAPIRef.current) return
    if (fetchStatus === 'fetching') {
      gridAPIRef.current.showLoadingOverlay()
    } else {
      gridAPIRef.current.hideOverlay()
    }
  }, [fetchStatus, gridAPIRef?.current])

  const overrideVolume = async (values, data, column) => {
    setVisiblePopoverId()
    gridAPIRef.current.showLoadingOverlay()
    const originalVolume = data.override
      ? data.override.original_volume
      : data.type === 'past'
        ? data.sales
        : data.forecast
    const payload = {
      store_number,
      tank_id: selectedManifold ? selectedManifold.manifold_id : selectedTank.tank_id,
      date: data.date,
      override_volume: values.override_volume,
      original_volume: isDefined(values.override_volume) ? originalVolume : null,
      exclude_from_forecast: values.exclude_from_forecast,
      exclude_forecast_from_sales: values.exclude_forecast_from_sales,
    }

    try {
      column === 'sales'
        ? await useSalesOverrideMutation.mutateAsync(payload)
        : await useForecastOverrideMutation.mutateAsync(payload)
    } catch (error) {
      console.log(error)
    } finally {
      gridAPIRef.current.hideOverlay()
    }
  }

  const deleteOverride = async (data, column) => {
    setVisiblePopoverId()
    gridAPIRef.current.showLoadingOverlay()

    const payload = {
      store_number,
      tank_id: selectedManifold ? selectedManifold.manifold_id : selectedTank.tank_id,
      date: data.date,
    }
    try {
      column === 'sales'
        ? await useDeleteSalesOverrideMutation.mutateAsync(payload)
        : await useDeleteForecastOverrideMutation.mutateAsync(payload)
    } catch (error) {
      ErrorNotification(error)
    } finally {
      gridAPIRef.current.hideOverlay()
    }
  }

  const selectTodayRow = (api) => {
    if (!api) return null
    api.ensureNodeVisible((node) => node.data?.type === 'today', 'middle')
    const rows = api.getRenderedNodes()
    const todaysRow = rows.find((row) => row.data?.type === 'today')
    if (todaysRow) {
      todaysRow.setSelected(true)
    }
  }
  const { updateSetting } = useUserSettings()

  const selectedData = useMemo(() => {
    if (forecastData) {
      return showProjected ? forecastData.daily_with_drops : forecastData.daily
    }
    return []
  }, [forecastData, showProjected])

  useEffect(() => {
    selectTodayRow(gridAPIRef?.current)
  }, [selectedData, gridAPIRef?.current])

  const { scopes } = useSecurityContext()
  const hasOverridePermission = useMemo(() => scopes?.site_monitoring?.forecaster_override, [scopes])

  const columnDefs = useMemo(
    () =>
      getColumnDefs({
        deleteOverride,
        overrideVolume,
        visiblePopoverId,
        setVisiblePopoverId,
        hasOverridePermission,
        storeTimeZone: store?.timezone,
        isManifoldChild: !!selectedTank?.manifold_id,
        varianceThreshold: config.forecast_variance_threshold,
      }),
    [deleteOverride, overrideVolume, visiblePopoverId, setVisiblePopoverId, hasOverridePermission, store, forecastData]
  )
  const getRowId = useCallback((row) => row.data?.date, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
    }),
    []
  )

  return (
    <Vertical flex={1}>
      <GraviGrid
        controlBarProps={{
          title: 'Historicals / Forecasts',
        }}
        sideBar={false}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        rowGroupPanelShow='never'
        rowHeight={30}
        isRowMaster={(data) => data.hourly.length}
        storageKey='tank-forecasts'
        rowData={selectedData}
        onFirstDataRendered={(params) => {
          selectTodayRow(params.api)
        }}
        externalRef={gridAPIRef}
        masterDetail
        detailCellRendererParams={{
          detailGridOptions: {
            rowSelection: 'multiple',
            suppressRowClickSelection: true,
            enableRangeSelection: true,
            pagination: false,
            paginationAutoPageSize: true,
            columnDefs: hourliesColumnDefs({ isHourlies: true }),
            defaultColDef: {
              sortable: true,
              resizable: true,
              flex: 1,
            },
          },
          getDetailRowData: (params) => {
            params.successCallback(params.data.hourly || [])
          },
        }}
        onGridConfigChanged={(config) => updateSetting('gridConfig::tank-forecasts', config)}
      />
    </Vertical>
  )
}

export function ForecastDisplayDate({ date, type, isHourlies = false, isDay = false }) {
  const forecastDate = moment(date)
  const day = formatters.LONG_DAY(forecastDate)
  const isWeekend = day === 'Sunday' || day === 'Saturday'
  const isPast = type === 'past'
  const textAppearance = isWeekend ? 'warning' : isPast ? 'medium' : 'default'
  const textStyle = isPast ? { fontStyle: 'italic' } : {}
  const textTransform = isHourlies ? 'uppercase' : 'capitalize'
  return (
    <Texto
      textTransform={textTransform}
      category='p2'
      weight={isPast ? 500 : 600}
      style={{ ...textStyle, display: 'flex', alignItems: 'center' }}
      appearance={textAppearance}
    >
      {isHourlies
        ? formatters.TIME(forecastDate)
        : isDay
          ? formatters.DAY(forecastDate)
          : formatters.DATE(forecastDate)}
    </Texto>
  )
}
export function VarianceCellRenderer({ forecast, variance, threshold }) {
  const divisor = forecast === 0 ? 1 : forecast
  let percentage = (Math.round(variance) / Math.round(divisor)) * 100
  percentage = Math.abs(Math.round(percentage)) === 0 ? 0 : percentage
  const isPositive = variance >= 0
  const exceedsThreshold = percentage > threshold * 100

  return (
    <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
      <Texto className='mr-3'>
        {isPositive && '+'}
        {addCommasToNumber(variance, 0)}
      </Texto>
      <Texto appearance={exceedsThreshold ? 'error' : 'default'}>
        {Math.round(percentage) === 0 ? (
          ''
        ) : Math.round(percentage) > 0 ? (
          <CaretUpFilled className='mr-1' style={{ color: 'var(theme-color-success', fontSize: '0.7em' }} />
        ) : (
          <CaretDownFilled className='mr-1' style={{ color: 'var(theme-color-error', fontSize: '0.7em' }} />
        )}
        {addCommasToNumber(Math.abs(percentage), 0)}%
      </Texto>
    </div>
  )
}
